import {
  INCOMING_CREDIT_DOCUMENT_TYPE,
  INCOMING_INVOICE_DOCUMENT_TYPE,
  isIncomingCreditDocumentType,
  isIncomingInvoiceDocumentType,
} from './incomingInvoice';

/**
 * Umwandlung Ausgangs- <-> Eingangsbeleg (falsch einsortierter Upload).
 * GoBD: der Ursprung wird nie umgeschrieben, sondern abgelehnt und verweist auf den neuen Beleg.
 */
export type InvoiceReclassifyTarget = 'incoming' | 'outgoing';

export type InvoiceReclassifyRow = {
  document_type?: string | null;
  status?: string | null;
  source?: string | null;
  mime_type?: string | null;
  filename?: string | null;
  has_ledger_snapshot?: boolean | null;
  reclassified_to_id?: string | null;
};

const OUTGOING_RECLASSIFIABLE_TYPES = new Set([
  'invoice',
  'partial_invoice',
  'final_invoice',
  'credit_note',
  'invoice_correction',
]);

/** Nur hochgeladene Belege; vom System erzeugte Rechnungen sind echte Ausgangsrechnungen. */
const OUTGOING_UPLOAD_SOURCES = new Set(['ai_upload', 'manual_upload']);

function isPdf(row: InvoiceReclassifyRow): boolean {
  const mime = String(row.mime_type ?? '').toLowerCase();
  if (mime === 'application/pdf') return true;
  return !mime && String(row.filename ?? '').toLowerCase().endsWith('.pdf');
}

/** Spalte ist NOT NULL DEFAULT '{}' – leeres Objekt heißt: kein Snapshot (Upload). */
export function hasLedgerSnapshot(value: unknown): boolean {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.keys(value as Record<string, unknown>).length > 0;
}

export function invoiceReclassifyTarget(row: InvoiceReclassifyRow): InvoiceReclassifyTarget | null {
  const type = String(row.document_type ?? '');
  if (isIncomingInvoiceDocumentType(type)) return 'outgoing';
  if (OUTGOING_RECLASSIFIABLE_TYPES.has(type)) return 'incoming';
  return null;
}

/** null = umwandelbar, sonst deutscher Grund. */
export function invoiceReclassifyBlockReason(row: InvoiceReclassifyRow): string | null {
  const target = invoiceReclassifyTarget(row);
  if (!target) return 'Dieser Belegtyp kann nicht umgewandelt werden.';
  if (String(row.reclassified_to_id ?? '').trim()) return 'Dieser Beleg wurde bereits umgewandelt.';
  if (row.status === 'cancelled') return 'Stornierte Belege können nicht umgewandelt werden.';
  if (target === 'incoming') {
    if (!OUTGOING_UPLOAD_SOURCES.has(String(row.source ?? ''))) {
      return 'Nur hochgeladene Ausgangsrechnungen können umgewandelt werden. Erstellte Rechnungen bitte stornieren.';
    }
    if (row.has_ledger_snapshot) {
      return 'Erstellte Rechnungen können nicht umgewandelt werden. Bitte stornieren.';
    }
  } else if (!isPdf(row)) {
    return 'Nur PDF-Belege können als Ausgangsrechnung übernommen werden.';
  }
  return null;
}

export function reclassifiedDocumentType(documentType: string | null | undefined): string {
  const type = String(documentType ?? '');
  if (isIncomingInvoiceDocumentType(type)) {
    return isIncomingCreditDocumentType(type) ? 'invoice_correction' : 'invoice';
  }
  return type === 'credit_note' || type === 'invoice_correction'
    ? INCOMING_CREDIT_DOCUMENT_TYPE
    : INCOMING_INVOICE_DOCUMENT_TYPE;
}

export function invoiceReclassifyActionLabel(target: InvoiceReclassifyTarget): string {
  return target === 'incoming' ? 'In Eingangsrechnung umwandeln' : 'In Ausgangsrechnung umwandeln';
}
